import { useMutation } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useNavigate, useOutletContext } from 'react-router';
import { useIotsApi } from '../../../contexts/iotsApi';
import { useLanguage } from '../../../contexts/language';
import { disableNavigationGuard } from '../../../hooks/useNavigationGuard';
import ContractBasicInfoForm from './ContractBasicInfoForm';
import { ContractTemplateContext } from './types';
import useApiErrorMessageGetter from '../../../hooks/useApiErrorMessage';

const CreateContractPage = () => {
  const { createContract } = useIotsApi();
  const { t } = useLanguage();
  const mutation = useMutation({ mutationFn: createContract });
  const { enqueueSnackbar } = useSnackbar();
  const getErrorMessage = useApiErrorMessageGetter();
  const navigate = useNavigate();
  const { setDisplayedContractName } =
    useOutletContext<ContractTemplateContext>();

  return (
    <ContractBasicInfoForm
      contract={null}
      isLoading={mutation.isPending}
      onContractNameChange={setDisplayedContractName}
      onSubmit={values => {
        mutation.mutate(
          {
            name: values.name,
            operatorId: values.operatorId,
            dateFrom: values.startDate,
            dateTo: values.endDate,
            serviceType: values.serviceType,
            allowedPorts: values.allowedPortsIds,
            defaultPortOfDepartureId: values.defaultDeparturePortId,
            defaultPortOfArrivalId: values.defaultArrivalPortId,
            county: values.county,
          },
          {
            onSuccess: id => {
              enqueueSnackbar({
                variant: 'success',
                message: t('content.contract-creation-success'),
              });

              void disableNavigationGuard(() =>
                navigate(`/admin/contracts/${id}`),
              );
            },
            onError: error => {
              enqueueSnackbar({
                variant: 'error',
                message: getErrorMessage(
                  error,
                  t('content.contract-creation-error'),
                ),
              });
              mutation.reset();
            },
          },
        );
      }}
      onCancel={() => {
        void navigate('/admin/contracts');
      }}
    />
  );
};

export default CreateContractPage;
